// @ts-check
import { Col, Collapse, Icon, Input, Row, Select } from 'antd';
import Form from '@/src/shared/components/Form';
import React, { useCallback, useEffect, useState } from 'react';
import Heading from '@/src/shared/components/Heading';
import { form as formStyle } from '@/styles';
import style from '@/styles/utilities/searchFilters';
import { useRouter } from "next/router";
import fuzzysort from 'fuzzysort'
import formatURL from "@/utils/formatURL";
import WhitepaperAttachments from "./WhitepaperAttachments";

/** @type {import("antd/lib/collapse").CollapseProps} */
const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

/** 
 * @param {{
 *   whitepapers: import("../../../types").MediaLibraryItem[];
 *   loading: boolean;
 * }} props
*/
export default function Whitepaper(props) {
  const { loading, whitepapers } = props;
  const router = useRouter();
  const { query } = router;

  const [isOpen, setIsOpen] = useState(true);
  const [mounted, setMounted] = useState(false);

  // filters
  const [search, setSearch] = useState('');
  const [current, setCurrent] = useState(1);
  const [selectedTags, setSelectedTags] = useState([]);

  // initialized filters based on url query
  useEffect(() => {
    // if (mounted) return;
    if (!query) return;

    if (query.q && typeof query.q === 'string') {
      setSearch(query.q);
    }
    if (query.tags) {
      setSelectedTags(Array.isArray(query.tags) ? query.tags : query.tags.split(','));
    }
    if (query.page && !Number.isNaN(Number(query.page))) {
      setCurrent(Number(query.page));
    }

    // setMounted(true);
  }, [query.q, query.tags, query.page]);

  const tagOptions = whitepapers.reduce(/** @param {string[]} acc */ (acc, cur) => {
    return Array.from(new Set(acc.concat(cur.Tags)));
  }, []);

  // filter results based on Archived flag and UI filters
  let filteredWhitepapers = whitepapers.filter(w => w.Archived.Code === 'N');
  if (selectedTags.length) {
    filteredWhitepapers = filteredWhitepapers.filter((wp) => wp.Tags.some((t) => selectedTags.includes(t)));
  }
  if (search) {
    let results = fuzzysort.go(
      `${search}`, 
      filteredWhitepapers, 
      {keys: ['FileTitle', 'FileName'], all: true, threshold: 0.35}
    );
    filteredWhitepapers = Array.from(results).sort((a, b) => a.score - b.score).map(r => r.obj);
  }

  const updateURL = useCallback((updates) => {
    let query = {
      page: current,
      q: search,
      tags: selectedTags ?? [],
      ...updates
    };
    if (query.page === 1) delete query.page;
    if (!query.q) delete query.q;
    if (!selectedTags.length) delete query.tags;
    router.replace({ query }, null, { shallow: true });
  }, [current, search, selectedTags]);


  const handleFiltersCollapse = activeTabs => {
    setIsOpen(activeTabs?.length > 0)
  };

  return (
    <Row gutter={24}>
      <Col xs={24} md={isOpen ? 12 : 24} lg={isOpen ? 8 : 24}>
        <div css={style.filters}>
          <Collapse
            defaultActiveKey={['1']}
            {...filterCollapseProps}
            onChange={handleFiltersCollapse}
            style={{backgroundColor: '#fff'}}
          >
            <Collapse.Panel
              key="1"
              header={
                <Heading type={3} weight="bolder" theme="primaryGray">
                  Search Filters
                </Heading>
              }
              id={isOpen ? 'collapsepanel' : 'expandpanel'}
            >
              {loading ? null : (
                <Form>
                  <Form.Item>
                    <Input
                      prefix={<Icon type="search" />}
                      placeholder="Search"
                      css={formStyle.inputGray}
                      defaultValue={search}
                      onChange={({ target: { value }}) => {
                        setSearch(value);
                        setCurrent(1);
                        updateURL({ q: value, page: 1 });
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Type">
                    <Select
                      css={formStyle.inputGray}
                      size="large"
                      placeholder={'Select Type'}
                      allowClear
                      id='type'
                      style={{width: '100%'}}
                      onChange={/** @param {string[]} value */(value, s) => {
                        setSelectedTags(value);
                        setCurrent(1);
                        updateURL({ tags: value, page: 1 });
                      }}
                      showArrow
                      mode='multiple'
                      defaultValue={selectedTags.length ? selectedTags : undefined}
                    >
                      {tagOptions.map(opt => (
                        <Select.Option key={opt} value={opt}>
                          {opt}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>              
                </Form>
              )}
            </Collapse.Panel>
          </Collapse>
        </div>
      </Col>
      <Col xs={24} md={isOpen ? 12 : 24} lg={isOpen ? 16 : 24}>
        <WhitepaperAttachments
          results={filteredWhitepapers}
          current={current}
          handlePaginationChange={(p) => {
            setCurrent(p);
            updateURL({ page: p });
          }}
        />
      </Col>
    </Row>
  );
};
